import type { CityConfig } from "../city-template/types";

export const longmontConfig: CityConfig = {
  city: "Longmont",
  state: "Colorado",
  stateAbbr: "CO",
  slug: "longmont",
  route: "/longmont",
  title: "Emergency Passport Longmont, CO — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Longmont, CO — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Boulder and Weld County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Longmont, CO",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Longmont and northern Front Range travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Longmont%20Colorado%20downtown%20with%20historic%20Main%20Street%2C%20Front%20Range%20mountain%20views%20with%20Longs%20Peak%20visible%20in%20the%20distance%2C%20Boulder%20County%20plains%2C%20warm%20golden%20hour%20light%20over%20the%20northern%20Colorado%20city%2C%20charming%20urban%20district%20with%20mountain%20backdrop%2C%20professional%20cityscape%20photography%2C%20warm%20amber%20tones%20with%20cool%20blue%20mountain%20hues%2C%20no%20visible%20text%20or%20people%2C%20cinematic%20wide%20angle%20view&width=1800&height=700&seq=longmont-hero-001&orientation=landscape",
  heroImageAlt: "Longmont Colorado downtown with Front Range mountains and Longs Peak in the background",
  geoPosition: "40.1672;-105.1019",
  geoPlacename: "Longmont, Colorado",
  lastModified: "2026-06-03",
  regionalAgency: {
    name: "Colorado Passport Agency",
    address: "7245 S Havana Street Suite B-600, Centennial, CO 80112",
    distance: "Approximately 50 miles south — about 1 hour via US-36 or I-25",
  },
  nearbyCitiesZips: [
    { name: "Downtown Longmont", zips: "80501, 80502" },
    { name: "East Longmont", zips: "80504" },
    { name: "North Longmont", zips: "80503" },
    { name: "South Longmont", zips: "80501, 80503" },
    { name: "Niwot", zips: "80503" },
    { name: "Lyons", zips: "80540" },
    { name: "Firestone", zips: "80504, 80520" },
    { name: "Mead", zips: "80542" },
    { name: "Frederick", zips: "80516, 80530" },
    { name: "Erie", zips: "80516" },
    { name: "Dacono", zips: "80514" },
    { name: "Berthoud", zips: "80513" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Longmont departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Boulder", route: "/boulder" },
    { name: "Emergency passport help in Denver", route: "/denver" },
    { name: "Urgent passport services in Fort Collins", route: "/fort-collins" },
    { name: "Same-day passport help in Erie", route: "/longmont" },
    { name: "Expedited passport services in Firestone", route: "/longmont" },
    { name: "Passport courier services in Niwot", route: "/longmont" },
    { name: "Rush passport help in Lyons", route: "/longmont" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Longmont remotely via secure courier. We do not have a walk-in office in Longmont — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Longmont and northern Front Range travelers without the drive to Denver.",
  fpcOfficeAddress: "7245 S Havana Street Suite B-600, Centennial, CO 80112",
  fpcOfficeDistance: "Serving all of Longmont, Boulder County, and the northern Front Range",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Longmont and the northern Front Range. Longmont is Boulder County's second-largest city and a major technology and manufacturing hub. From downtown Main Street to the east Longmont tech corridor, from Niwot to Lyons — we help Longmont travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Longmont neighborhoods and nearby communities including Niwot, Lyons, Firestone, Frederick, Erie, and Berthoud. If you are in the Longmont area or northern Front Range, we have you covered.",
  howItWorksIntro: "Most Longmont travelers complete everything online and ship documents overnight. No need to drive to Boulder or Denver for a passport appointment. Our emergency and expedited passport process is designed for Longmont travelers who need fast, reliable results.",
  mistakesIntro: "With tech workers, outdoor recreation enthusiasts, and students in the Longmont area, northern Front Range travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Longmont travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Longmont? Here are the most common ones we hear from Boulder County travelers.",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (CDPHE)", url: "https://cdphe.colorado.gov/vital-records", desc: "Colorado Department of Public Health & Environment for vital records statewide" },
    { label: "Marriage Licenses (Boulder County Clerk)", url: "https://www.bouldercounty.gov/", desc: "Boulder County Clerk & Recorder for Longmont area" },
    { label: "Divorce Decrees (Boulder County District Court)", url: "https://www.courts.state.co.us/Courts/District/Index.cfm?District_ID=20", desc: "Boulder County District Court for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Colorado vital records online via VitalChek" },
  ],
  statePageRoute: "/states/colorado",
  statePageName: "Colorado",
  dmvWebsite: "https://dmv.colorado.gov",
  dmvLabel: "Colorado DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Longmont international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Sarah T.",
      location: "Downtown Longmont, CO",
      rating: 5,
      text: "Needed to renew for a business trip to Germany and discovered my passport expired. Fast Passport Center had a renewal back to me in 4 days. No need to drive to Denver.",
      service: "Emergency Renewal",
    },
    {
      name: "Jason K.",
      location: "East Longmont, CO",
      rating: 5,
      text: "My family had a trip to Costa Rica and my son needed his first passport. Fast Passport Center guided us through the entire process — both parents, the right forms — and had it done in 5 days.",
      service: "Child Passport",
    },
    {
      name: "Amanda W.",
      location: "Niwot, CO",
      rating: 5,
      text: "Lost my passport before a conference abroad. Fast Passport Center handled the DS-64 replacement and had a new one in 2 days. Outstanding service.",
      service: "Lost Passport",
    },
    {
      name: "Dr. Mark L.",
      location: "Firestone, CO",
      rating: 5,
      text: "I travel internationally for research several times a year and Fast Passport Center is always my first call. Faster than driving to Boulder, cheaper than FedEx resellers.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "lgm-1",
      name: "Longmont Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 Coffman St",
      city: "Longmont",
      state: "CO",
      zip: "80501",
      phone: "(303) 772-2570",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Longmont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for Longmont residents. Appointment strongly recommended. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "lgm-2",
      name: "Longmont East Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1650 Pace St",
      city: "Longmont",
      state: "CO",
      zip: "80504",
      phone: "(303) 651-0910",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from east Longmont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for east Longmont and Firestone residents. Call ahead to confirm hours.",
      icon: "ri-mail-line",
    },
    {
      id: "lgm-3",
      name: "Erie Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "540 Mitchell St",
      city: "Erie",
      state: "CO",
      zip: "80516",
      phone: "(303) 828-5440",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7 miles from central Longmont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Erie and southeastern Longmont residents. Limited passport appointment slots.",
      icon: "ri-mail-line",
    },
    {
      id: "lgm-4",
      name: "Berthoud Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "225 Bunyan Ave",
      city: "Berthoud",
      state: "CO",
      zip: "80513",
      phone: "(970) 532-2344",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles north of Longmont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for north Longmont and Berthoud residents. Accepts walk-ins but appointments preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "lgm-5",
      name: "Lafayette Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "409 W. Emma St",
      city: "Lafayette",
      state: "CO",
      zip: "80026",
      phone: "(303) 665-7496",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6 miles southwest of Longmont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southwest Longmont and Lafayette residents.",
      icon: "ri-mail-line",
    },
    {
      id: "lgm-6",
      name: "Boulder County Clerk — Longmont",
      type: "County Clerk Passport Acceptance",
      address: "529 Coffman St",
      city: "Longmont",
      state: "CO",
      zip: "80501",
      phone: "(303) 441-3500",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.5 miles from downtown Longmont",
      website: "https://www.bouldercounty.org/",
      websiteLabel: "Boulder County Clerk",
      notes: "Boulder County satellite office in Longmont. Accepts walk-ins but appointments recommended.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Aurora (50 mi)", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Longmont photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Longmont acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Longmont applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Denver regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Longmont families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Longmont travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Longmont." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Longmont address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Longmont?",
      a: "Fast Passport Center offers emergency passport processing in Longmont in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Longmont travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Longmont?",
      a: "Same-day passports are not available in Longmont. The closest regional passport agency is in Aurora (50 miles). For urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Longmont residents without making that drive.",
    },
    {
      q: "Where can I get a fast passport renewal in Longmont?",
      a: "Fast Passport Center offers fast passport renewal in Longmont with no office visit required. We serve all Longmont neighborhoods and zip codes. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Longmont?",
      a: "The fastest way to get an urgent passport in Longmont is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround.",
    },
    {
      q: "What if I live in a nearby town like Erie, Firestone, or Berthoud?",
      a: "We serve all of the Longmont area and surrounding communities including Niwot, Lyons, Firestone, Frederick, Erie, and Berthoud. Our overnight shipping labels work from any UPS location.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Longmont?",
      a: "Popular options include Longmont Main Post Office (80501), Longmont East Post Office (80504), Erie Post Office (80516), and Boulder County Clerk Longmont (80501). All require appointments.",
    },
    {
      q: "How much does expedited passport service cost in Longmont?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote.",
    },
    {
      q: "Do you help with passport photos in Longmont?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Longmont offer passport photo services. We review your photos before submission.",
    },
    {
      q: "What documents do I need for a first-time passport in Longmont?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist.",
    },
    {
      q: "Can you help with a child's passport in Longmont?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Longmont families through the entire process.",
    },
    {
      q: "Do I need to drive to Boulder or Denver to get an expedited passport from Longmont?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight from Longmont. Our couriers handle the in-person delivery to State Department facilities.",
    },
    {
      q: "Is Fast Passport Center cheaper than going to the regional agency?",
      a: "In most cases, yes. Our best price guarantee means we're typically 30–100% less expensive than FedEx, Staples, and other third-party passport courier resellers.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Longmont", address: "1551 Hover St, Longmont, CO 80501", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Main St", address: "301 Main St, Longmont, CO 80501", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Longmont", address: "1480 Main St, Longmont, CO 80501", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Longmont", address: "1651 Hover St, Longmont, CO 80501", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Longmont", address: "745 E. Ken Pratt Blvd, Longmont, CO 80501", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Erie", address: "600 Austin Ave, Erie, CO 80516", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Lafayette", address: "960 N. 111th St, Lafayette, CO 80026", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Costco — Longmont", address: "1900 Commerce Dr, Longmont, CO 80501", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Longmont",
      address: "915 S. Main St, Longmont, CO 80501",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Lafayette",
      address: "137 E. South Boulder Rd, Lafayette, CO 80026",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Convenient for southwest Longmont residents. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Erie",
      address: "696 W. Jasper Rd, Erie, CO 80516",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Berthoud",
      address: "321 Mountain Ave, Berthoud, CO 80513",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for north Longmont and Berthoud residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "DEN",
      name: "Denver International Airport",
      distance: "50 miles from Longmont via US-36 or I-25",
      address: "8500 Peña Blvd, Denver, CO 80249",
      note: "The primary international airport for Longmont travelers. Bus service (BRT) available from Longmont to DEN via Boulder.",
      flightUrl: "https://www.flydenver.com/",
      flightLabel: "DEN Flight Info",
      featured: true,
    },
    {
      code: "COS",
      name: "Colorado Springs Airport",
      distance: "130 miles from Longmont via I-25",
      address: "7770 Milton E. Proby Pkwy, Colorado Springs, CO 80916",
      note: "Regional airport alternative for southern Colorado connections.",
      flightUrl: "https://www.coloradosprings.gov/airport",
      flightLabel: "Colorado Springs Airport",
      featured: false,
    },
  ],
};